/**
 * 검색 드롭다운 "HOT 카테고리" 알약 설정.
 *
 * 카테고리 정본은 `@/config/categories` 하나뿐이다. 여기서는 (a) 어떤 소분류를
 * 후보로 볼지, (b) 어떤 순서로 추릴지, (c) 좁은 알약에 들어갈 짧은 라벨만
 * 정한다. 이름/활성 여부/계층은 절대 여기서 복제하지 않는다.
 */

import { isLocale } from "@/i18n/routing";
import {
  getCategoryByCode,
  getCategoryName,
  getLevel3Categories,
  type CategoryItem,
} from "@/config/categories";

/** 메인 페이지 타일(CategoryVariantB.homeCategories)의 categoryCode. 타일이 바뀌면 여기도 맞춘다. */
export const HOT_CATEGORY_ROOT_CODES = [
  "20-01",
  "10-06-01",
  "10-01",
  "10-05-01",
  "30-01",
  "40-01",
  "50-01-03",
  "50-01-02", // 오토바이·스쿠터(09-28 통합, 옛 50-01-05)
] as const;

export const HOT_CATEGORY_MIN_PRODUCTS = 2;
export const HOT_CATEGORY_LIMIT = 8;

type LocaleKey = keyof CategoryItem["names"];

/** 지원하지 않는 로케일은 기본 로케일(ko)로 떨어뜨린다. */
function toLocaleKey(locale: string): LocaleKey {
  return isLocale(locale) ? locale : "ko";
}

/**
 * 루트가 중분류면 그 소분류(활성) 전부, 소분류면 자기 자신. 중복 제거, 루트 순서 유지.
 *
 * 카운트 RPC(`get_visible_product_counts_by_category`)는 소분류 코드 단위로만
 * 세므로 중분류 루트는 반드시 펼쳐서 넘겨야 한다.
 */
export function getHotCategoryCandidateCodes(): string[] {
  const codes: string[] = [];
  const seen = new Set<string>();

  for (const rootCode of HOT_CATEGORY_ROOT_CODES) {
    const root = getCategoryByCode(rootCode);
    if (!root || !root.is_active) continue;

    const targets =
      root.level === 3 ? [root] : getLevel3Categories(rootCode);

    for (const target of targets) {
      if (!target.is_active || seen.has(target.code)) continue;
      seen.add(target.code);
      codes.push(target.code);
    }
  }

  return codes;
}

export interface HotCategoryCount {
  code: string;
  productCount: number;
}

/** 최소 개수 미만 제외 → productCount 내림차순, 동률은 candidate 순서 → limit. counts 에 없는 후보는 0 으로 본다. */
export function selectHotCategories(
  counts: ReadonlyArray<HotCategoryCount>,
  candidates: readonly string[],
  opts?: { min?: number; limit?: number },
): HotCategoryCount[] {
  const min = opts?.min ?? HOT_CATEGORY_MIN_PRODUCTS;
  const limit = opts?.limit ?? HOT_CATEGORY_LIMIT;

  const byCode = new Map(counts.map((c) => [c.code, c.productCount]));

  return candidates
    .map((code, order) => ({
      code,
      productCount: byCode.get(code) ?? 0,
      order,
    }))
    .filter((entry) => entry.productCount >= min)
    .sort((a, b) => b.productCount - a.productCount || a.order - b.order)
    .slice(0, limit)
    .map(({ code, productCount }) => ({ code, productCount }));
}

/** 짧은 표시명 오버라이드(정본 이름이 긴 것만). */
const SHORT_NAMES: Record<string, CategoryItem["names"]> = {
  "10-05-01": {
    ko: "한복·기모노",
    en: "Hanbok / Kimono",
    ja: "チマチョゴリ·着物",
    "zh-CN": "韩服·和服",
    "zh-TW": "韓服·和服",
  },
};

/** 반각·전각 괄호 묶음 — "즉석카메라 (폴라로이드)" 같은 부연 설명. */
const PARENTHETICAL = /[(（][^)）]*[)）]/g;

/** " / " 구분자 (앞뒤 공백 유무 무관). */
const SLASH_SEPARATOR = /\s*\/\s*/g;

/**
 * 알약 라벨: 오버라이드 → 없으면 정본 이름에서 괄호 부분 제거 후 " / " 를 en 은 " / " 그대로, 그 외 로케일은 "·" 로.
 * 예) 10-01-03 ko "오즈모·짐벌", en "Osmo / Gimbal"; 10-01-06 ko "즉석카메라"; 20-01-01 ko "삼성 갤럭시".
 * 알 수 없는 코드는 코드 문자열 반환.
 */
export function getHotCategoryLabel(code: string, locale: string): string {
  const localeKey = toLocaleKey(locale);

  const override = SHORT_NAMES[code];
  if (override) return override[localeKey];

  const category = getCategoryByCode(code);
  if (!category) return code;

  const separator = localeKey === "en" ? " / " : "·";

  return getCategoryName(category, localeKey)
    .replace(PARENTHETICAL, "")
    .replace(SLASH_SEPARATOR, separator)
    .trim();
}
